/**
 * Typed client for the SeneSight REST API (E7). Every response is the provenance+confidence envelope
 * the API guarantees; errors are structured (never a silent empty). A 401 surfaces distinctly so the
 * app can route back to login (the session expired).
 */

export interface ProvenanceSummary {
  readonly source: string;
  readonly method: string;
  readonly confidence: number;
}

export interface Envelope<T> {
  readonly data: T;
  readonly meta: {
    readonly provenance: ProvenanceSummary[];
    readonly confidence: number;
    readonly asOf: string | null;
    readonly nextCursor?: string | null;
  };
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

const API_BASE = "/api/v1";

export interface ApiOptions {
  readonly token: string | null;
  readonly signal?: AbortSignal;
  /**
   * JSON request body for a mutation. Omitted entirely for a GET, and for the POST routes whose
   * whole input is in the path (the review-queue confirm/reject pair) — sending `undefined` as a
   * body would put the literal string "undefined" on the wire.
   */
  readonly body?: unknown;
}

async function request<T>(
  method: "GET" | "POST" | "DELETE",
  path: string,
  opts: ApiOptions,
): Promise<Envelope<T>> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method,
      headers: {
        "content-type": "application/json",
        accept: "application/json",
        ...(opts.token ? { authorization: `Bearer ${opts.token}` } : {}),
      },
      signal: opts.signal,
      ...(opts.body === undefined ? {} : { body: JSON.stringify(opts.body) }),
    });
  } catch {
    throw new ApiError(0, "network_error", "could not reach the SeneSight API");
  }

  const body = (await res.json().catch(() => null)) as
    | Envelope<T>
    | { error?: { code?: string; message?: string } }
    | null;

  if (!res.ok) {
    const err = (body as { error?: { code?: string; message?: string } } | null)?.error;
    throw new ApiError(
      res.status,
      err?.code ?? "error",
      err?.message ?? `request failed (${res.status})`,
    );
  }
  return body as Envelope<T>;
}

export async function apiGet<T>(path: string, opts: ApiOptions): Promise<Envelope<T>> {
  return request("GET", path, opts);
}

export async function apiPost<T>(path: string, opts: ApiOptions): Promise<Envelope<T>> {
  return request("POST", path, opts);
}

export async function apiDelete(path: string, opts: ApiOptions): Promise<void> {
  await request("DELETE", path, opts);
}

/**
 * NX-3433: run an authenticated request; on a 401, renew the session once and retry the same
 * request with the fresh token. A renewal that fails (or a retry refused again) rethrows the
 * 401 — the session has then ended and the user is on the sign-in page.
 */
export async function withSessionRenewal<T>(
  token: string,
  onUnauthenticated: (refusedToken?: string) => Promise<string | null>,
  request: (token: string) => Promise<T>,
): Promise<T> {
  try {
    return await request(token);
  } catch (caught: unknown) {
    if (!(caught instanceof ApiError) || !caught.isUnauthenticated) throw caught;
    const fresh = await onUnauthenticated(token);
    if (!fresh) throw caught;
    try {
      return await request(fresh);
    } catch (retryError: unknown) {
      // Refused with a session issued moments ago: onUnauthenticated ends it (no loop).
      if (retryError instanceof ApiError && retryError.isUnauthenticated) {
        await onUnauthenticated(fresh);
      }
      throw retryError;
    }
  }
}
